import { LBtn, LInputField, Loader } from "@components";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApi } from "@services/useApi";
import { UserInfo, UserStatus, UserUpdate } from "@store/generalTypes";
import { useQuery } from "@utils/useQuery";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { object, SchemaOf, string } from "yup";
import { NavItems } from "../../../common/helpers/navigation";
import { SignupContainer } from "../components/SignupContainer";
import { useIsAuthenticated } from "@azure/msal-react";

const formSchema: SchemaOf<UserUpdate> = object({
    title: string().optional(),
    firstName: string().required("* required"),
    lastName: string().required("* required"),
    password: string()
        .required("* required")
        .min(8, "The password must be at least 8 characters")
        .test("isValidPass", "Your password must contain uppercase, lowercase and at least a number", (value) => {
            const hasUpperCase = /[A-Z]/.test(value!);
            const hasLowerCase = /[a-z]/.test(value!);
            const hasNumber = /[0-9]/.test(value!);
            let validConditions = 0;
            const numberOfMustBeValidConditions = 3;
            const conditions = [hasLowerCase, hasUpperCase, hasNumber];
            conditions.forEach((condition) => (condition ? validConditions++ : null));
            if (validConditions >= numberOfMustBeValidConditions) {
                return true;
            }
            return false;
        })
});

export const SetupAccount = (): JSX.Element => {
    const [isLoading, setIsLoading] = useState(true);
    const [userInfo, setUserInfo] = useState<UserInfo>();
    const { allParams } = useQuery();
    const navigate = useNavigate();
    const { getUser, loading } = useApi();
    const isAuthenticated = useIsAuthenticated();

    const validateMethods = useForm<UserUpdate>({
        resolver: yupResolver(formSchema)
    });

    useEffect(() => {
        void loadUserForm();
    }, []);

    const loadUserForm = useCallback(async () => {
        if (allParams.email) {
            const user = await getUser(allParams.email);
            const isFromInvite = allParams.organizationId && allParams.locationId && allParams.roleId;

            if (isFromInvite && user?.status === UserStatus.Active) {
                navigate(NavItems.Signup.Terms.path, {
                    state: {
                        updateAccountInfo: null,
                        email: allParams.email,
                        organizationId: allParams.organizationId,
                        locationId: allParams.locationId,
                        roleId: allParams.roleId,
                        orgAdmin: !!allParams.orgAdmin,
                        userId: user.id
                    }
                });
            } else if (user.status === UserStatus.Active || isAuthenticated) {
                navigate(NavItems.Login.Landing.path);
            }

            setUserInfo(user);
            setIsLoading(false);
        } else {
            navigate(NavItems.Login.Landing.path);
        }
    }, []);

    const formSubmitHandler: SubmitHandler<UserUpdate> = async (data: UserUpdate) => {
        navigate(NavItems.Signup.Terms.path, {
            state: {
                updateAccountInfo: data,
                email: allParams.email,
                organizationId: allParams.organizationId,
                locationId: allParams.locationId,
                roleId: allParams.roleId,
                orgAdmin: !!allParams.orgAdmin,
                userId: userInfo!.id
            }
        });
    };

    if (isLoading) {
        return (
            <SignupContainer>
                <Loader />
            </SignupContainer>
        );
    }

    return (
        <FormProvider {...validateMethods}>
            <SignupContainer>
                <form className="p-6" onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                    <div className="field pb-5">
                        <label className="label is-small">{"Email Address"}</label>
                        <label className="label">{allParams.email}</label>
                    </div>
                    <div className="columns">
                        <div className="column pb-0 is-2">
                            <LInputField
                                type="text"
                                label={"Title"}
                                inputName="title"
                                placeholder=""
                                isExpanded={true}
                                defaultValue={userInfo!.title}
                                value={userInfo!.title}
                            />
                        </div>
                        <div className="column pb-0">
                            <LInputField
                                type="text"
                                label={"First name"}
                                inputName="firstName"
                                placeholder="e.g. John"
                                isExpanded={true}
                                defaultValue={userInfo!.firstName}
                                value={userInfo!.firstName}
                            />
                        </div>
                        <div className="column pb-0">
                            <LInputField
                                type="text"
                                label={"Last name"}
                                inputName="lastName"
                                placeholder="e.g. Smith"
                                isExpanded={true}
                                defaultValue={userInfo!.lastName}
                                value={userInfo!.lastName}
                            />
                        </div>
                    </div>
                    <LInputField
                        type="password"
                        label="Create a password"
                        inputName="password"
                        placeholder="********"
                    />
                    <LBtn fullWidth={true} type="submit" loading={loading}>
                        {"Continue to Terms and Conditions"}
                    </LBtn>
                </form>
            </SignupContainer>
        </FormProvider>
    );
};
